import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query"; // 🚀 React Query untuk caching
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
// import { useTrip } from "../v1/masterData/TripContext";
import { debounce } from "lodash";
import { useTrip } from "contexts/v1/masterData/TripContext";
import { useSearchParams } from "react-router-dom";
import { usePaymentMethod } from "contexts/v1/masterData/PaymentMethodContext";

// Membuat context untuk Accounting Backup
const AccountingBackupContext = createContext();

export const AccountingBackupProvider = ({ children }) => {
  const { trips, updateFilterTrip } = useTrip();
  const { paymentMethods: paymentMethodsObj } = usePaymentMethod();
  const allPaymentMethods = paymentMethodsObj?.data || [];
  const [searchParams, setSearchParams] = useSearchParams();

  const today = new Date();
  const localDate = today.toLocaleDateString("en-CA"); // "YYYY-MM-DD" format sesuai lokal timezone
  // buat variabel untuk menampilkan tgl dan waktu
  const localDateTime = today.toLocaleString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  // console.log("localDateTime", localDateTime);

  const initialDate = searchParams.get("date") || localDate;

  const [filterOptions, setFilterOptions] = useState({
    search: searchParams.get("search") || "",
    currentPage: 1,
    totalPages: 1,
    filters: {
      start_date: initialDate, 
      end_date: initialDate,
      trip_id: "",
      agent_id: "",
      customer_id: "",
      label_id: "",
      mode: "",
      status: "finished",
      guest_name: "",
      payment_method_id: "", // Add payment method filter
      type: "", // Add type filter (BM/mm/j)
    },
    sortOrder: "desc", // Add sort order
  });

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isOpenCanvas, setIsOpenCanvas] = useState(false);

  // 🔹 Gunakan useMemo untuk optimasi trips
  const sessions = useMemo(() => {
    return trips.data.map((trip) => ({
      id: trip.id,
      title: trip.name,
      // time: `${trip.start_time} - ${trip.end_time}`,
      time: `${trip.start_time.slice(0, 5)} - ${trip.end_time.slice(0, 5)}`,
      stocks: {
        stocks: trip.availability,
        booking: trip.total_reserved,
        // arrived: trip.total_confirmed,
        arrived:
          (trip.total_confirmed || 0) +
          (trip.total_played || 0) +
          (trip.total_finished || 0),
        in_track: trip.remaining,
      },
      date: filterOptions.filters.start_date,
      bg_trip:
        {
          "Trip 1": "bg-yellow-lt",
          "Trip 2": "bg-purple-lt",
          "Trip 3": "bg-blue-lt",
          "Trip 4": "bg-green-lt",
          "Trip 5": "bg-red-lt",
        }[trip.name] || "bg-secondary-lt",
    }));
  }, [trips.data, filterOptions.filters.start_date]);

  // 🔹 Optimasi updateFilter agar tidak menyebabkan re-render yang tidak perlu
  const updateFilter = useCallback(
    (key, value) => {
      setFilterOptions((prevState) => ({
        ...prevState,
        filters: {
          ...prevState.filters,
          [key]: value,
        },
      }));

      if (key === "start_date") {
        updateFilterTrip("date", value);
        setSearchParams((prev) => {
          prev.set("date", value);
          return prev;
        });
      }
    },
    [updateFilterTrip, setSearchParams]
  );

  // 🔹 Add method to update sort order
  const updateSortOrder = useCallback((order) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      sortOrder: order,
    }));
  }, []);

  // 🔹 Gunakan debounce untuk optimasi pencarian (lebih efisien)
  const handleSearchDebounced = useCallback(
    debounce((value) => {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: value,
      }));
    }, 300),
    []
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    handleSearchDebounced(value);
    setSearchParams((prev) => {
      if (value) prev.set("search", value);
      else prev.delete("search");
      return prev;
    });
  };

  const fetchBookings = async () => {
    const { start_date, end_date, agent_id, label_id, status, payment_method_id, type } =
      filterOptions.filters;
    // console.log("filterOptions.filters", filterOptions.filters);
    const queryString = buildQueryString({
      search: filterOptions.search || "",
      page: filterOptions.currentPage,
      limit: 350, // 🔥 Pagination lebih kecil untuk load lebih cepat
      start_date: start_date,
      end_date: end_date,
      ...(status && { status }),
      ...(agent_id && { agent_id: agent_id }),
      ...(payment_method_id && {
        payment_method_id: payment_method_id,
      }),
      ...(type && { type: type }),
      sort: filterOptions.sortOrder, // Use dynamic sort order
      orderBy: "code", // Add explicit order by code as requested
    });

    // const response = await getRequest(`/reservation${queryString}`);
    const response = await getRequest(`/reservation-income-v2${queryString}`);

    if (response.success) {
      return response;
    } else {
      throw new Error("Failed to fetch bookings");
    }
  };

  // 🔥 Gunakan React Query (FORMAT V5) untuk caching & refetch otomatis
  const {
    data: bookings,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["bookingsBackup", filterOptions], // ✅ Format V5
    queryFn: fetchBookings, // ✅ Nama key harus "queryFn"
    keepPreviousData: true, // ✅ Data lama tetap ditampilkan sambil load baru
    staleTime: 15 * 60 * 1000, // ✅ Cache selama 15 menit
  });

  // const {
  //   data: bookings,
  //   isLoading,
  //   error,
  //   refetch,
  // } = useQuery({
  //   queryKey: ["bookings", filterOptions.filters.reservation_date], // ✅ Tambahkan tanggal ke queryKey
  //   queryFn: fetchBookings,
  //   keepPreviousData: true,
  //   staleTime: 30000,
  // });

  // 🔥 Kelompokkan data per trip agar load lebih cepat
  const dataPerTrip = useMemo(() => {
    if (!bookings?.data) return {};
    return bookings.data.reduce((acc, item) => {
      const tripId = item.trip_id || "Unknown Trip";
      if (!acc[tripId]) acc[tripId] = [];
      acc[tripId].push(item);
      return acc;
    }, {});
  }, [bookings]);

  // 🔥 Kelompokkan data per payment method untuk accounting
  const dataPerPaymentMethod = useMemo(() => {
    if (!bookings?.data) return {};
    return bookings.data.reduce((acc, item) => {
      const paymentMethodName =
        item.payment_method_name || "Unknown Payment Method";
      if (!acc[paymentMethodName]) {
        acc[paymentMethodName] = {
          id: item.payment_method?.id || null,
          name: paymentMethodName,
          data: [],
          totals: {
            totalPrice: 0,
            netPrice: 0,
            susukAgent: 0,
            others: 0,
            count: 0,
          },
        };
      }
      acc[paymentMethodName].data.push(item);

      // Calculate totals with safe number conversion
      const totalPrice = Number(item.total_price) || 0;
      const netPrice = Number(item.net_price_by_include_susuk) || 0;
      const susukAgent = Number(item.susuk_agent) || 0;
      const qtyActivities = Number(item.qty_product_activities) || 0;

      acc[paymentMethodName].totals.totalPrice += totalPrice;
      acc[paymentMethodName].totals.netPrice += netPrice;
      acc[paymentMethodName].totals.susukAgent += susukAgent;
      acc[paymentMethodName].totals.others += qtyActivities * 50000;
      acc[paymentMethodName].totals.count += 1;

      return acc;
    }, {});
  }, [bookings]);

  // 🔥 Gunakan useMemo untuk menghitung total ATV Single & Tandem (hanya untuk status "finished")
  const { singleTotal, tandemTotal } = useMemo(() => {
    let totalATVSingle = 0;
    let totalATVTandem = 0;

    if (bookings?.data) {
      bookings.data
        .filter((reservation) => reservation.status === "finished") // ✅ Hanya hitung status "finished"
        .forEach((reservation) => {
          reservation.activities.forEach((activity) => {
            if (activity.activity === "ATV Single") {
              totalATVSingle += activity.qty;
            }
            if (activity.activity === "ATV Tandem") {
              totalATVTandem += activity.qty;
            }
          });
        });
    }

    return { singleTotal: totalATVSingle, tandemTotal: totalATVTandem };
  }, [bookings]); // ✅ Akan dihitung ulang saat `bookings` berubah

  const { singleTotalFinished, tandemTotalFinished } = useMemo(() => {
    let totalATVSingle = 0;
    let totalATVTandem = 0;

    if (bookings?.data) {
      bookings.data
        .filter((reservation) => reservation.status === "finished") // Filter status "finished"
        .forEach((reservation) => {
          reservation.activities.forEach((activity) => {
            if (activity.activity === "ATV Single") {
              totalATVSingle += activity.qty;
            }
            if (activity.activity === "ATV Tandem") {
              totalATVTandem += activity.qty;
            }
          });
        });
    }

    return {
      singleTotalFinished: totalATVSingle,
      tandemTotalFinished: totalATVTandem,
    };
  }, [bookings]); // Akan dihitung ulang saat `bookings` berubah

  const { singleTotalReconfirmed, tandemTotalReconfirmed } = useMemo(() => {
    let totalATVSingle = 0;
    let totalATVTandem = 0;

    if (bookings?.data) {
      bookings.data
        .filter((reservation) => reservation.status === "reconfirmed") // Filter status "reconfirmed"
        .forEach((reservation) => {
          reservation.activities.forEach((activity) => {
            if (activity.activity === "ATV Single") {
              totalATVSingle += activity.qty;
            }
            if (activity.activity === "ATV Tandem") {
              totalATVTandem += activity.qty;
            }
          });
        });
    }

    return {
      singleTotalReconfirmed: totalATVSingle,
      tandemTotalReconfirmed: totalATVTandem,
    };
  }, [bookings]); // Akan dihitung ulang saat `bookings` berubah

  // single dan tandem play
  const { singleTotalPlay, tandemTotalPlay } = useMemo(() => {
    let totalATVSingle = 0;
    let totalATVTandem = 0;

    if (bookings?.data) {
      bookings.data
        .filter((reservation) => reservation.status === "played") // Filter status "play"
        .forEach((reservation) => {
          reservation.activities.forEach((activity) => {
            if (activity.activity === "ATV Single") {
              totalATVSingle += activity.qty;
            }
            if (activity.activity === "ATV Tandem") {
              totalATVTandem += activity.qty;
            }
          });
        });
    }

    return { singleTotalPlay: totalATVSingle, tandemTotalPlay: totalATVTandem };
  }, [bookings]); // Akan dihitung ulang saat `bookings` berubah

  // total adalah diluar booking.id, isinya object
  const total = useMemo(() => {
    if (!bookings?.total) return 0;
    return bookings.total;
  }, [bookings]);

  return (
    <AccountingBackupContext.Provider
      value={{
        sessions,
        isOpenCanvas,
        setIsOpenCanvas,
        selectedBooking,
        setSelectedBooking,
        bookings,
        isLoading,
        error,
        filterOptions,
        updateFilter,
        updateSortOrder, // 🔥 Add sort order method
        handleSearch,
        dataPerTrip,
        dataPerPaymentMethod, // 🔥 Add payment method grouping
        refetch, // 🔥 Bisa dipakai untuk refresh data manual
        trips,
        singleTotal, // 🔥 Tambahkan ke context
        tandemTotal, // 🔥 Tambahkan ke context
        singleTotalFinished, // Tambahkan ke context
        tandemTotalFinished, // Tambahkan ke context
        singleTotalReconfirmed, // Tambahkan ke context
        tandemTotalReconfirmed, // Tambahkan ke context
        singleTotalPlay, // Tambahkan ke context
        tandemTotalPlay, // Tambahkan ke context
        total,
        allPaymentMethods, // 🔥 Add full list of payment methods
      }}
    >
      {children}
    </AccountingBackupContext.Provider>
  );
};

// 🔹 Hook untuk mengakses AccountingBackupContext
export const useAccountingBackup = () => {
  const context = useContext(AccountingBackupContext);
  if (!context) {
    throw new Error("useAccountingBackup must be used within a AccountingBackupProvider");
  }
  return context;
};
